import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { CreditCard, Money, CheckCircle, Spinner, Crown, Phone } from "@phosphor-icons/react";
import { runBrowserHandoffs } from "@/utils/webusbPrinter";
import LoyaltyMemberPanel from "@/components/payment/LoyaltyMemberPanel";
import CardPanel, { PinpadPanel } from "@/components/payment/CardPanel";
import CashPanel from "@/components/payment/CashPanel";
import GiftCardPanel from "@/components/payment/GiftCardPanel";
import TipStep from "@/components/payment/TipStep";
import SuccessStep from "@/components/payment/SuccessStep";
import LoyaltyStep from "@/components/payment/LoyaltyStep";

const STEPS = { TIP: "tip", LOYALTY: "loyalty", PAY: "pay", WAIT: "waiting", SUCCESS: "success" };
const CHANNEL_NAME = "crest-customer-display";

// Authorize.net Accept.js — the browser tokenization library. Loads the
// right host (production vs. sandbox) based on the config the backend
// resolves at runtime, so admins can swap between environments by
// changing a setting in the UI (no redeploy needed).
const ACCEPT_JS_HOST = {
  production: "https://js.authorize.net/v1/Accept.js",
  sandbox: "https://jstest.authorize.net/v1/Accept.js",
};

// Fetched on first tokenize attempt, cached for the session. The backend
// returns { api_login_id, public_client_key, environment } — never the
// transaction key. If credentials are unset the object is still returned
// but with empty strings so we can surface a clear error to the cashier.
let _acceptJsConfig = null;
let _acceptJsConfigPromise = null;
const fetchAcceptJsConfig = async () => {
  if (_acceptJsConfig) return _acceptJsConfig;
  if (_acceptJsConfigPromise) return _acceptJsConfigPromise;
  _acceptJsConfigPromise = axios
    .get(`${API}/payment-gateway/public`)
    .then((r) => {
      _acceptJsConfig = r.data || { api_login_id: "", public_client_key: "", environment: "production" };
      return _acceptJsConfig;
    })
    .catch(() => {
      _acceptJsConfigPromise = null;
      return { api_login_id: "", public_client_key: "", environment: "production" };
    });
  return _acceptJsConfigPromise;
};
// Invalidate on window focus so key rotations from the Settings page take
// effect on other tabs without needing a manual reload.
if (typeof window !== "undefined") {
  window.addEventListener("focus", () => { _acceptJsConfig = null; _acceptJsConfigPromise = null; });
}

let _acceptJsLoadPromise = null;
const loadAcceptJs = async (environment) => {
  if (typeof window === "undefined") return false;
  if (window.Accept) return true;
  if (_acceptJsLoadPromise) return _acceptJsLoadPromise;
  const url = ACCEPT_JS_HOST[environment === "sandbox" ? "sandbox" : "production"];
  _acceptJsLoadPromise = new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = url;
    s.async = true;
    s.charset = "utf-8";
    s.onload = () => resolve(!!window.Accept);
    s.onerror = () => { _acceptJsLoadPromise = null; resolve(false); };
    document.head.appendChild(s);
  });
  return _acceptJsLoadPromise;
};

// Tokenize card data via Accept.js. Supports two modes:
//   • manual entry — pass { cardNumber, month, year, cardCode }
//   • card-present swipe — pass { trackData } with { track1, track2 }
// Returns { ok:true, opaque } on success, or
// { ok:false, stage: "creds"|"load"|"dispatch", message, code } on failure.
// PAN/CVV/track data never leave the browser — Accept.js sends them
// straight to Authorize.net's tokenization endpoint.
const tokenizeWithAcceptJs = async (input) => {
  const config = await fetchAcceptJsConfig();
  if (!config.api_login_id || !config.public_client_key) {
    return {
      ok: false,
      stage: "creds",
      message:
        "Payment processor isn't configured. An admin needs to enter the " +
        "Authorize.net API Login ID + Public Client Key under Settings → " +
        "Company → Payment Gateway.",
    };
  }
  const ok = await loadAcceptJs(config.environment);
  // Belt-and-braces: the <script> onload fires before Accept.js finishes
  // wiring dispatchData onto window.Accept in some browser/network combos.
  // Poll briefly so we don't hit E_WC_03 (internal library error) purely
  // because we called dispatchData too soon.
  if (ok && !(window.Accept && typeof window.Accept.dispatchData === "function")) {
    for (let i = 0; i < 20 && !(window.Accept?.dispatchData); i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 100));
    }
  }
  if (!ok || !window.Accept || typeof window.Accept.dispatchData !== "function") {
    return {
      ok: false,
      stage: "load",
      message:
        "Card processing library (Accept.js) failed to load. This is almost " +
        "always a network issue reaching js.authorize.net — check the terminal's " +
        "internet connection, then retry. Ad-blockers and content filters can also " +
        "block this script.",
    };
  }
  const payload = {
    authData: {
      clientKey: config.public_client_key,
      apiLoginID: config.api_login_id,
    },
  };
  if (input.trackData) {
    // Card-present / swipe path. Authorize.net accepts a raw track string
    // and skips the CVV requirement entirely (CVV isn't encoded on the
    // magstripe — real POS terminals never ask for it after a swipe).
    payload.trackData = input.trackData;
  } else {
    // Accept.js's `cardData` mode: `cardCode` (CVV) is OPTIONAL. When the
    // caller omits it (e.g. after a magstripe swipe on a CNP merchant
    // account where Authorize.net rejects `trackData` with a spurious
    // "Card Processor Unreachable" / E_WC_03 / E_WC_14) we still tokenize
    // successfully using just PAN + expiration.
    const card = {
      cardNumber: input.cardNumber,
      month: input.month,
      year: input.year,
    };
    if (input.cardCode) card.cardCode = input.cardCode;
    payload.cardData = card;
  }
  return new Promise((resolve) => {
    window.Accept.dispatchData(payload, (response) => {
      if (response?.messages?.resultCode === "Ok" && response.opaqueData) {
        resolve({
          ok: true,
          opaque: {
            dataDescriptor: response.opaqueData.dataDescriptor,
            dataValue: response.opaqueData.dataValue,
          },
        });
        return;
      }
      const err = response?.messages?.message?.[0] || {};
      const code = err.code || "unknown";
      const text = err.text || "";
      // Log the FULL response so on-site support can copy/paste the exact
      // error into a bug report — the toast text alone hides the details.
      console.error("Accept.js tokenize failed:", { code, text, response });
      // E_WC_03 / E_WC_14 (and any response containing "unreachable") are
      // library / reachability failures, not card declines. Surface them
      // as network problems with actionable fix hints instead of the
      // scary "Card was declined" copy that made cashiers re-swipe good
      // cards.
      const isReachabilityErr =
        code === "E_WC_03" ||
        code === "E_WC_14" ||
        /unreachable|processor/i.test(text);
      if (isReachabilityErr) {
        resolve({
          ok: false,
          stage: "load",
          code,
          message:
            (text ? `${text} ` : "") +
            "Accept.js couldn't reach Authorize.net's tokenization endpoint. " +
            "Common causes: (1) the POS lost internet for a moment — retry once; " +
            "(2) an ad-blocker or content filter is blocking js.authorize.net or " +
            "api2.authorize.net; (3) the API Login ID / Client Key don't match the " +
            "environment (sandbox vs production). If it repeats on every swipe, " +
            "verify REACT_APP_AUTHNET_LOGIN_ID + REACT_APP_AUTHNET_CLIENT_KEY.",
        });
        return;
      }
      if (code === "E_WC_04" || code === "E_WC_01") {
        resolve({
          ok: false,
          stage: "load",
          code,
          message:
            (text || "Accept.js couldn't authenticate your Authorize.net keys.") +
            " Double-check the Client Key + API Login ID in frontend/.env match " +
            "your merchant account and environment (sandbox vs production).",
        });
        return;
      }
      resolve({
        ok: false,
        stage: "dispatch",
        code,
        message:
          text ||
          "Authorize.net rejected the card data. Double-check the card number, " +
          "expiration and CVV and try again.",
      });
    });
  });
};

const PaymentModal = ({ open, onClose, order, onComplete }) => {
  // Wizard
  const [step, setStep] = useState(STEPS.TIP);
  const [posSettings, setPosSettings] = useState(null);
  // Payment-gateway status — powers the sandbox-mode warning banner on
  // the PAY step. Fetched every time the modal opens so a mid-shift
  // credential rotation flips the banner off without a page reload.
  const [gatewayStatus, setGatewayStatus] = useState(null);
  const channelRef = useRef(null);
  const popupRef = useRef(null);
  const cfdSaveTimerRef = useRef(null);
  // Once the operator hits "Cancel & do it here" in the WAIT step, we run
  // the WHOLE checkout on the POS and stop re-opening the CFD on this
  // order. Ref (not state) so the flag survives async pop-up handoffs
  // without triggering another render.
  const cfdDeclinedRef = useRef(false);

  // Tip
  const [tipAmount, setTipAmount] = useState(0);
  const [customTipOpen, setCustomTipOpen] = useState(false);
  const [customTipInput, setCustomTipInput] = useState("");

  // Loyalty
  const [loyaltyPhone, setLoyaltyPhone] = useState("");
  const [loyaltyLookupLoading, setLoyaltyLookupLoading] = useState(false);
  const [loyaltyMember, setLoyaltyMember] = useState(null);     // existing match
  const [loyaltyDeclined, setLoyaltyDeclined] = useState(false);
  const [showEnroll, setShowEnroll] = useState(false);
  const [enrollName, setEnrollName] = useState("");
  const [enrollEmail, setEnrollEmail] = useState("");
  const [redeemPoints, setRedeemPoints] = useState(0);          // redeemed in 100-point chunks
  const [memberPerks, setMemberPerks] = useState({ info_perks: [], redeemable_perks: [] });
  const [selectedPerk, setSelectedPerk] = useState(null);       // the one tier-perk being applied

  // Pay
  const [paymentMethod, setPaymentMethod] = useState("card");
  // Gift-card payment tender state.
  const [gcCode, setGcCode] = useState("");
  const [gcLookup, setGcLookup] = useState(null);   // resolved gift card object
  const [gcLookupErr, setGcLookupErr] = useState("");
  const [gcLookingUp, setGcLookingUp] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  // Persistent error banner (survives the toast dismiss) so operators can
  // actually READ what went wrong instead of watching it flash by.
  const [paymentError, setPaymentError] = useState(null);
  const [cardNumber, setCardNumber] = useState("");
  const [expDate, setExpDate] = useState("");
  const [cvv, setCvv] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  // Raw magstripe tracks captured on swipe. When present the card is
  // treated as CARD PRESENT — no CVV required and Accept.js tokenizes via
  // `trackData` rather than `cardData`. Cleared by clearCardFields().
  const [swipeTracks, setSwipeTracks] = useState(null);

  // Fetch tip presets + CFD flag + gateway status once per open.
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/pos-settings`).then(r => setPosSettings(r.data)).catch(() => setPosSettings({
      tip_preset_1: 15, tip_preset_2: 18, tip_preset_3: 20, customer_display_enabled: false,
    }));
    // Sandbox-warning banner: sandbox = true when the backend has no live
    // Auth.net creds OR the merchant explicitly picked "sandbox" env.
    axios.get(`${API}/payment-gateway/status`)
      .then(r => setGatewayStatus(r.data))
      .catch(() => setGatewayStatus({ configured: false, sandbox: true, environment: "sandbox" }));
  }, [open]);

  // NEW: When the modal opens AND a customer display is configured, skip
  // the POS-side tip/loyalty steps and hand off to the customer immediately.
  // Servers explicitly asked NOT to be shown a tip picker first — the
  // customer should always be the one choosing.
  //
  // `openedForOrderRef` guards against re-firing the popup handoff on every
  // re-render / on step transitions inside the same modal lifecycle.
  const openedForOrderRef = useRef(null);
  useEffect(() => {
    if (!open || !posSettings || !order) return;
    if (!posSettings.customer_display_enabled) return;
    if (openedForOrderRef.current === order.id) return;
    if (cfdDeclinedRef.current) return;  // operator already opted out
    openedForOrderRef.current = order.id;
    (async () => {
      const opened = await openCustomerDisplayPopup();
      if (!opened) {
        // Popup blocked / no CFD available — fall back to POS-side flow.
        setStep(STEPS.TIP);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, posSettings, order?.id]);

  // BroadcastChannel listener for customer-display popup results.
  useEffect(() => {
    if (!open) return;
    const ch = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current = ch;
    ch.onmessage = (ev) => {
      const msg = ev.data || {};
      if (msg.type !== "wizard_complete" || msg.orderId !== order?.id) return;
      // Apply choices and advance to pay step.
      setTipAmount(msg.payload.tip_amount || 0);
      setLoyaltyMember(msg.payload.loyalty_member || null);
      setLoyaltyDeclined(!!msg.payload.loyalty_declined);
      setRedeemPoints(msg.payload.redeem_points || 0);
      setStep(STEPS.PAY);
      // Send the popup back to idle (carousel) instead of closing it — the
      // window stays open all session so the NEXT checkout is instant.
      try { ch.postMessage({ type: "end_order" }); } catch { /* ignore */ }
    };
    return () => { ch.close(); channelRef.current = null; };
  }, [open, order?.id]);

  // -------------------------------------------------------------------------
  // Magswipe keyboard-wedge auto-fill (Volcora built-in reader et al)
  // -------------------------------------------------------------------------
  // A generic USB/PS-2 magstripe reader emulates a keyboard. When a card is
  // swiped it fires ~100+ keydown events in <500ms terminated by Enter. The
  // Track 1 payload begins with `%B` and Track 2 with `;`. We buffer keys
  // globally, reset the buffer after any idle >500ms, and once the burst
  // stops (Enter or timeout) we attempt to parse and auto-fill the card
  // fields. Only active on the PAY step with the Card tab — cash / other
  // methods don't need a swipe. Placed BEFORE the early-return below so
  // hook order stays stable across renders.
  useEffect(() => {
    if (!open || step !== STEPS.PAY || paymentMethod !== "card") return;

    let buffer = "";
    let idleTimer = null;

    const parseMagstripe = (raw) => {
      // Preserve the raw tracks — we pass them straight to Accept.js in
      // trackData form so Authorize.net treats this as a CARD PRESENT
      // transaction and doesn't require CVV.
      const t1full = raw.match(/(%B\d{13,19}\^[^^]{0,60}\^\d{4}[^?]*\??)/);
      const t2full = raw.match(/(;\d{13,19}=\d{4}[^?]*\??)/);
      const track1 = t1full ? t1full[1] : "";
      const track2 = t2full ? t2full[1] : "";

      // Track 1: %B<PAN>^<LNAME>/<FNAME>^<YYMM><SVC><discretionary>?
      const t1 = raw.match(/%B(\d{13,19})\^([^^]{0,60})\^(\d{2})(\d{2})/);
      if (t1) {
        const [ln = "", fn = ""] = (t1[2] || "").split("/").map((s) => s.trim());
        return {
          pan: t1[1],
          yy: t1[3],
          mm: t1[4],
          firstName: fn,
          lastName: ln,
          track1,
          track2,
        };
      }
      // Track 2: ;<PAN>=<YYMM><discretionary>?
      const t2 = raw.match(/;(\d{13,19})=(\d{2})(\d{2})/);
      if (t2) {
        return {
          pan: t2[1], yy: t2[2], mm: t2[3],
          firstName: "", lastName: "",
          track1, track2,
        };
      }
      return null;
    };

    const formatPan = (pan) => pan.replace(/(\d{4})(?=\d)/g, "$1 ").trim();

    const tryFlush = () => {
      const parsed = parseMagstripe(buffer);
      buffer = "";
      if (!parsed) return;
      setCardNumber(formatPan(parsed.pan));
      setExpDate(`${parsed.mm}/${parsed.yy}`);
      if (parsed.firstName) setFirstName(parsed.firstName);
      if (parsed.lastName) setLastName(parsed.lastName);
      setCvv("");
      // Persist the raw tracks — they get sent to Accept.js in trackData
      // form so this counts as CARD PRESENT and needs no CVV. Operator can
      // immediately hit "Pay $X".
      setSwipeTracks({ track1: parsed.track1 || "", track2: parsed.track2 || "" });
      setPaymentError(null);
      toast.success(
        `Card swiped: •••• ${parsed.pan.slice(-4)}${parsed.firstName || parsed.lastName ? ` (${parsed.firstName || parsed.lastName})` : ""}. Ready to pay — CVV not required for swiped cards.`,
        { duration: 6000 }
      );
    };

    const onKey = (e) => {
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      if (e.key === "Enter") {
        if (buffer.length > 20) {
          e.preventDefault();
          tryFlush();
        }
        return;
      }
      if (e.key.length !== 1) return;
      buffer += e.key;
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        if (buffer.length > 20) tryFlush();
        else buffer = "";
      }, 500);
    };

    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      clearTimeout(idleTimer);
    };
  }, [open, step, paymentMethod]);

  if (!order || !posSettings) {
    // Render an inert dialog while settings load to preserve animation.
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[400px]" data-testid="payment-modal-loading">
          <VisuallyHidden asChild><DialogTitle>Loading payment</DialogTitle></VisuallyHidden>
          <div className="py-12 text-center text-muted-foreground">
            <Spinner size={32} className="animate-spin mx-auto mb-3" />
            Loading…
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const subtotal = order.subtotal || 0;
  const tax = order.tax || 0;
  const discountFromPoints = Math.min(redeemPoints / 100, subtotal); // 100 pts = $1
  const perkDiscount = selectedPerk
    ? (selectedPerk.type === "percent"
        ? Math.min(subtotal * (selectedPerk.value / 100), subtotal)
        : Math.min(selectedPerk.value, subtotal))
    : 0;
  const adjustedSubtotal = Math.max(0, subtotal - discountFromPoints - perkDiscount);
  // Cash Discount / Dual Pricing — non-cash tenders pay the card processing
  // fee on top of the sticker price. Cash + gift-card sit outside the
  // surcharge because they don't cost the merchant an interchange fee.
  const cashDiscountEnabled = !!posSettings.cash_discount_enabled;
  const cashDiscountPct = Number(posSettings.cash_discount_percentage || 0);
  const cashDiscountLabel = posSettings.cash_discount_label || "Non-Cash Adjustment";
  const surchargeApplies =
    cashDiscountEnabled &&
    cashDiscountPct > 0 &&
    paymentMethod !== "cash" &&
    paymentMethod !== "gift_card";
  const preSurchargeTotal = adjustedSubtotal + tax + tipAmount;
  const cashDiscountSurcharge = surchargeApplies
    ? Math.round(preSurchargeTotal * (cashDiscountPct / 100) * 100) / 100
    : 0;
  const total = preSurchargeTotal + cashDiscountSurcharge;

  const tipPresets = [
    posSettings.tip_preset_1 || 15,
    posSettings.tip_preset_2 || 18,
    posSettings.tip_preset_3 || 20,
  ];

  const reset = () => {
    setStep(STEPS.TIP);
    setTipAmount(0);
    setCustomTipOpen(false);
    setCustomTipInput("");
    setLoyaltyPhone("");
    setLoyaltyMember(null);
    setLoyaltyDeclined(false);
    setShowEnroll(false);
    setEnrollName("");
    setEnrollEmail("");
    setRedeemPoints(0);
    setMemberPerks({ info_perks: [], redeemable_perks: [] });
    setSelectedPerk(null);
    setPaymentMethod("card");
    setGcCode("");
    setGcLookup(null);
    setGcLookupErr("");
    setCardNumber("");
    setExpDate("");
    setCvv("");
    setFirstName("");
    setLastName("");
    setSwipeTracks(null);
    setIsProcessing(false);
    setPaymentError(null);
    toast.dismiss("card-error-toast");
    // Allow the next opening of this order to re-trigger the CFD hand-off.
    openedForOrderRef.current = null;
    cfdDeclinedRef.current = false;
    // Return the CFD popup (if still open) to idle-carousel mode so the
    // customer isn't staring at a stale checkout view.
    try {
      const ch = channelRef.current || new BroadcastChannel(CHANNEL_NAME);
      ch.postMessage({ type: "end_order" });
    } catch { /* channel gone */ }
  };

  // Card-only clear — used by Back/Cancel paths so PAN/CVV never sit in
  // memory when the operator isn't actively entering a card. Also wipes
  // the raw swipe tracks so a re-entry starts from a clean CARD NOT PRESENT
  // state instead of accidentally reusing the previous swipe.
  const clearCardFields = () => {
    setCardNumber("");
    setExpDate("");
    setCvv("");
    setFirstName("");
    setLastName("");
    setSwipeTracks(null);
    setPaymentError(null);
    toast.dismiss("card-error-toast");
  };

  const close = () => {
    if (isProcessing) return;
    try { popupRef.current?.close(); } catch { /* noop */ }
    reset();
    onClose();
  };

  // -------- Customer display popup --------
  // The two browser-level reasons the popup historically failed to stick to
  // the 2nd monitor:
  //
  //   1. window.open() with a NAME that matches an already-open window
  //      silently re-uses that window and IGNORES the features string —
  //      so left/top/width/height are dropped. Fix: close the previous
  //      popup before opening, OR use a unique name each time.
  //
  //   2. Chrome ignores left/top in the features string unless the
  //      Window Management API permission has been granted, or the bounds
  //      fall fully inside a screen the browser already knows about. Fix:
  //      after opening, also call win.moveTo() + win.resizeTo() which are
  //      honored by Chrome on dual-monitor setups, and opportunistically
  //      use the Window Management API for first-time defaults.
  const CFD_GEOM_KEY = "cfd_popup_geometry";

  const readSavedGeometry = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(CFD_GEOM_KEY) || "null");
      if (saved && Number.isFinite(saved.left) && Number.isFinite(saved.top)) return saved;
    } catch { /* ignore */ }
    return null;
  };

  // Pick a sensible first-time origin. If the Window Management API is
  // available and the user has granted permission, place the popup on the
  // largest non-primary screen at that screen's full available size.
  // Otherwise fall back to a MAXIMIZED window on the primary screen —
  // customer displays are meant to fill their monitor, not squeeze into
  // an arbitrary 900×1100 window.
  const computeDefaultGeometry = async () => {
    const fallback = {
      left: 0,
      top: 0,
      width: typeof screen !== "undefined" ? screen.availWidth : 1280,
      height: typeof screen !== "undefined" ? screen.availHeight : 800,
    };
    try {
      if (window.getScreenDetails) {
        const details = await window.getScreenDetails();
        const others = (details.screens || []).filter((s) => !s.isPrimary);
        if (others.length) {
          const target = others.sort((a, b) => (b.width * b.height) - (a.width * a.height))[0];
          return {
            left: target.availLeft,
            top: target.availTop,
            width: target.availWidth,
            height: target.availHeight,
          };
        }
      }
    } catch { /* permission denied or unsupported — fall back */ }
    return fallback;
  };

  const openCustomerDisplayPopup = async () => {
    if (!posSettings.customer_display_enabled) return false;
    // Second-screen guard — even with the checkbox on, don't spawn a
    // popup on single-screen terminals (it just floats on top of the
    // POS and steals focus at every checkout).
    const { hasSecondScreen } = await import("@/utils/screenDetect");
    if (!(await hasSecondScreen())) {
      toast.info("Customer Display skipped — no 2nd screen detected on this terminal");
      return false;
    }
    // Operator has already taken over on this checkout — never bounce
    // them back into the CFD flow, even if they re-click Next: Loyalty.
    if (cfdDeclinedRef.current) return false;

    // Reuse a popup that's still alive from an earlier checkout. The CFD
    // page sits in IDLE mode between orders, so we just broadcast the new
    // order id over BroadcastChannel and let it switch views — no window
    // creation, no nav. Sub-millisecond hand-off.
    const existing = window.__cfd_popup;
    if (existing && !existing.closed) {
      popupRef.current = existing;
      try {
        const ch = channelRef.current || new BroadcastChannel(CHANNEL_NAME);
        ch.postMessage({ type: "start_order", orderId: order.id });
      } catch { /* opener may have lost the channel */ }
      try { existing.focus(); } catch { /* ignore */ }
      setStep(STEPS.WAIT);
      return true;
    }

    // BroadcastChannel handshake: another tab (or a POS reload) may have
    // opened a CFD popup we lost the handle to. Ping the channel and
    // wait ~250 ms for a "cfd_alive" reply before falling back to
    // window.open(). Prevents duplicate popups after a hard refresh.
    const handshakeAck = await new Promise((resolve) => {
      let done = false;
      const ch = new BroadcastChannel(CHANNEL_NAME);
      const finish = (ack) => {
        if (done) return;
        done = true;
        try { ch.close(); } catch { /* ignore */ }
        resolve(ack);
      };
      ch.onmessage = (ev) => {
        if (ev.data?.type === "cfd_alive") finish(true);
      };
      try {
        ch.postMessage({ type: "cfd_ping" });
      } catch { finish(false); return; }
      setTimeout(() => finish(false), 250);
    });
    if (handshakeAck) {
      // A CFD is out there — just push the new order to it via the
      // same channel. We don't get the popup handle back (window.open
      // returns null when a same-named window is claimed cross-context),
      // but the CFD will receive the start_order message and render.
      try {
        const ch = channelRef.current || new BroadcastChannel(CHANNEL_NAME);
        ch.postMessage({ type: "start_order", orderId: order.id });
      } catch { /* ignore */ }
      setStep(STEPS.WAIT);
      return true;
    }

    const geom = readSavedGeometry() || (await computeDefaultGeometry());
    const { left, top, width, height } = geom;

    const features = [
      `width=${width}`, `height=${height}`,
      `left=${left}`, `top=${top}`,
      "menubar=no", "toolbar=no", "location=no", "status=no",
      "popup=yes",
    ].join(",");
    const url = `/customer-display?order_id=${encodeURIComponent(order.id)}`;

    try {
      const win = window.open(url, "_blank", features);
      if (!win) {
        toast.error("Popup blocked. Allow pop-ups for the customer display.");
        return false;
      }
      popupRef.current = win;
      window.__cfd_popup = win;  // share across PaymentModal re-mounts

      // Hard-position the window. Chrome respects moveTo/resizeTo on
      // dual-monitor setups even when it ignored left/top in features.
      const enforce = (attempt = 0) => {
        try {
          if (win.closed) return;
          win.moveTo(left, top);
          win.resizeTo(width, height);
        } catch { /* cross-origin during nav — ignore */ }
        if (attempt < 3) setTimeout(() => enforce(attempt + 1), 200);
      };
      enforce();

      // Poll position every 2 s while the popup is open so a drag to a
      // new screen is captured even without the explicit Pin button.
      if (cfdSaveTimerRef.current) clearInterval(cfdSaveTimerRef.current);
      cfdSaveTimerRef.current = setInterval(() => {
        if (!win || win.closed) {
          clearInterval(cfdSaveTimerRef.current);
          cfdSaveTimerRef.current = null;
          if (window.__cfd_popup === win) window.__cfd_popup = null;
          return;
        }
        try {
          if (Number.isFinite(win.screenX) && Number.isFinite(win.screenY)) {
            localStorage.setItem(CFD_GEOM_KEY, JSON.stringify({
              left: win.screenX,
              top: win.screenY,
              width: win.outerWidth,
              height: win.outerHeight,
            }));
          }
        } catch { /* quota / cross-origin — ignore */ }
      }, 2000);

      setStep(STEPS.WAIT);
      return true;
    } catch {
      toast.error("Failed to open customer display window.");
      return false;
    }
  };

  // -------- Tip helpers --------
  const applyTipPct = (pct) => {
    setTipAmount(Math.round(subtotal * pct) / 100 * 100 / 100); // round to cents
    setTipAmount(parseFloat((subtotal * pct / 100).toFixed(2)));
  };

  const commitCustomTip = () => {
    const v = parseFloat(customTipInput);
    if (Number.isFinite(v) && v >= 0) {
      setTipAmount(parseFloat(v.toFixed(2)));
      setCustomTipOpen(false);
      setCustomTipInput("");
    } else {
      toast.error("Enter a valid tip amount");
    }
  };

  // -------- Loyalty helpers --------
  const fetchMemberPerks = async (memberId) => {
    try {
      const r = await axios.get(`${API}/loyalty/perks/${memberId}`);
      setMemberPerks({
        info_perks: r.data.info_perks || [],
        redeemable_perks: r.data.redeemable_perks || [],
      });
    } catch {
      setMemberPerks({ info_perks: [], redeemable_perks: [] });
    }
  };

  const handleLookupPhone = async () => {
    const digits = loyaltyPhone.replace(/\D/g, "");
    if (digits.length < 7) {
      toast.error("Enter a valid phone number");
      return;
    }
    setLoyaltyLookupLoading(true);
    try {
      const r = await axios.get(`${API}/loyalty/lookup-phone/${encodeURIComponent(digits)}`);
      if (r.data.found) {
        setLoyaltyMember(r.data.customer);
        setShowEnroll(false);
        fetchMemberPerks(r.data.customer.id);
      } else {
        setLoyaltyMember(null);
        setShowEnroll(true);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lookup failed");
    } finally {
      setLoyaltyLookupLoading(false);
    }
  };

  const handleEnroll = async () => {
    const digits = loyaltyPhone.replace(/\D/g, "");
    if (!enrollName || !enrollEmail || digits.length < 7) {
      toast.error("Name, email, and phone are required");
      return;
    }
    try {
      const r = await axios.post(`${API}/loyalty/customers`, {
        name: enrollName, email: enrollEmail, phone: digits,
      });
      setLoyaltyMember(r.data);
      setShowEnroll(false);
      fetchMemberPerks(r.data.id);
      toast.success(`Welcome, ${enrollName}!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enrollment failed");
    }
  };

  const handleDeclineLoyalty = () => {
    setLoyaltyDeclined(true);
    setLoyaltyMember(null);
    setRedeemPoints(0);
    setStep(STEPS.PAY);
  };

  // -------- Payment processing --------
  const formatCardNumber = (v) => v.replace(/\D/g, "").replace(/(\d{4})(?=\d)/g, "$1 ").substring(0, 19);
  const formatExpDate = (v) => {
    const c = v.replace(/\D/g, "");
    return c.length >= 2 ? `${c.substring(0, 2)}/${c.substring(2, 4)}` : c;
  };
  const isCardValid = swipeTracks
    ? cardNumber.replace(/\s/g, "").length >= 13 && expDate.length === 5
    : (
        cardNumber.replace(/\s/g, "").length >= 13 &&
        expDate.length === 5 &&
        cvv.length >= 3 &&
        firstName.length > 0 &&
        lastName.length > 0
      );

  const handleProcessPayment = async () => {
    setPaymentError(null);
    setIsProcessing(true);
    try {
      const paymentData = {
        order_id: order.id,
        // Fold the cash-discount surcharge into ``amount`` so the gateway
        // charges the customer for it. It's also passed alongside so the
        // backend can split it out on transactions + reports.
        amount: adjustedSubtotal + tax + cashDiscountSurcharge,
        payment_method: paymentMethod,
        tip_amount: tipAmount,
        surcharge_amount: cashDiscountSurcharge,
        loyalty_customer_id: loyaltyMember?.id || null,  // link order ↔ member for receipt QR + reports
      };
      // ---------------------------------------------------------------
      // Pinpad path — no card fields on the POS at all. Fire the amount
      // at the terminal, wait synchronously for the customer to complete
      // on the physical device. Backend endpoint handles both Authorize.net
      // Semi-Integrated (any certified pinpad) and Generic HTTP.
      // ---------------------------------------------------------------
      if (paymentMethod === "pinpad") {
        let response;
        try {
          response = await axios.post(`${API}/payments/external-terminal`, {
            order_id: order.id,
            amount: adjustedSubtotal + tax,
            tip_amount: tipAmount,
            loyalty_customer_id: loyaltyMember?.id || null,
          }, { timeout: 120_000 });  // pinpads can take a while
        } catch (e) {
          const detail = e.response?.data?.detail || e.message || "Pinpad payment failed";
          const isNetwork = !e.response;
          const title = isNetwork ? "Pinpad unreachable" :
                         e.response?.status === 402 ? "Pinpad declined the card" :
                         "Pinpad payment error";
          setPaymentError({ title, detail, code: e.response?.status });
          toast.error(title, {
            description: detail + (e.response?.status ? ` (HTTP ${e.response.status})` : ""),
            duration: Infinity,
            id: "card-error-toast",
            closeButton: true,
          });
          setIsProcessing(false);
          return;
        }
        // Post-approval bookkeeping — award loyalty points on the paid
        // amount so the pinpad path stays feature-equivalent to card/cash.
        if (loyaltyMember) {
          try { await axios.post(`${API}/loyalty/earn`, { customer_id: loyaltyMember.id, order_total: adjustedSubtotal + tax }); } catch { /* non-fatal */ }
        }
        setStep(STEPS.SUCCESS);
        toast.success("Payment approved on pinpad!");
        setTimeout(() => { onComplete(response.data); reset(); }, 1600);
        return;
      }

      // ---------------------------------------------------------------
      // Gift Card path — settle from the balance stored on a gift card
      // row. Backend redemption is atomic; shortfall returns 402 and we
      // surface a "split with another tender" toast.
      // ---------------------------------------------------------------
      if (paymentMethod === "gift_card") {
        if (!gcCode || !gcLookup) {
          toast.error("Look up the gift card first");
          setIsProcessing(false);
          return;
        }
        try {
          const response = await axios.post(`${API}/payments/process`, {
            ...paymentData,
            gift_card_code: gcCode.trim(),
          });
          if (loyaltyMember) {
            try { await axios.post(`${API}/loyalty/earn`, { customer_id: loyaltyMember.id, order_total: adjustedSubtotal + tax }); } catch { /* non-fatal */ }
          }
          if (loyaltyMember && redeemPoints > 0) {
            try { await axios.post(`${API}/loyalty/redeem`, { customer_id: loyaltyMember.id, points: redeemPoints }); } catch { /* non-fatal */ }
          }
          setStep(STEPS.SUCCESS);
          const remaining = response.data?.gift_card_remaining_balance;
          toast.success(
            typeof remaining === "number"
              ? `Redeemed — $${remaining.toFixed(2)} left on the card`
              : "Gift card redeemed",
          );
          setTimeout(() => { onComplete(response.data); reset(); }, 1600);
        } catch (e) {
          const detail = e.response?.data?.detail || e.message || "Redemption failed";
          const title = e.response?.status === 402 ? "Gift card can't cover the total" : "Gift card error";
          setPaymentError({ title, detail, code: e.response?.status });
          toast.error(title, { description: detail, duration: Infinity, id: "gc-error-toast", closeButton: true });
          setIsProcessing(false);
        }
        return;
      }

      if (paymentMethod === "card") {
        // Card-present path: on swipe we tokenize the parsed PAN +
        // expiration in cardData mode WITHOUT CVV. This works for both
        // Card-Present and Card-Not-Present Authorize.net merchant
        // accounts and side-steps the E_WC_03 / "Card Processor
        // Unreachable" errors that Accept.js throws for CNP merchants
        // when the deprecated `trackData` payload is used.
        const tokenizeOnce = () => {
          if (swipeTracks && (swipeTracks.track1 || swipeTracks.track2)) {
            const [m, y] = expDate.split("/");
            return tokenizeWithAcceptJs({
              cardNumber: cardNumber.replace(/\s/g, ""),
              month: m,
              year: `20${y}`,
              // Deliberately no cardCode — swipe is card-present, CVV is
              // NOT encoded on the magstripe, and Accept.js treats
              // cardData without cardCode as valid.
            });
          }
          const [m, y] = expDate.split("/");
          return tokenizeWithAcceptJs({
            cardNumber: cardNumber.replace(/\s/g, ""),
            month: m,
            year: `20${y}`,
            cardCode: cvv,
          });
        };
        let result = await tokenizeOnce();
        // Transient reachability errors (E_WC_03 / E_WC_14) usually clear
        // on a second try 800ms later — retry silently once before
        // bothering the operator with a scary error banner.
        if (!result.ok && result.stage === "load" &&
            (result.code === "E_WC_03" || result.code === "E_WC_14")) {
          await new Promise((r) => setTimeout(r, 800));
          result = await tokenizeOnce();
        }
        if (!result.ok) {
          const title =
            result.stage === "load"     ? "Card processor unreachable" :
            result.stage === "creds"    ? "Payment not configured"     :
            result.stage === "dispatch" ? "Card was declined at tokenization" :
                                          "Card processing error";
          setPaymentError({ title, detail: result.message, code: result.code });
          // Persistent toast (duration Infinity → require manual dismiss)
          toast.error(title, {
            description: result.message + (result.code ? ` (code ${result.code})` : ""),
            duration: Infinity,
            id: "card-error-toast",  // dedupe across retries
            closeButton: true,
          });
          setIsProcessing(false);
          return;
        }
        paymentData.opaque_data = result.opaque;
        // Cardholder name kept off the network: skip the legacy credit_card
        // payload entirely. (Backend prefers opaque_data anyway.)
      }
      const response = await axios.post(`${API}/payments/process`, paymentData);

      // For cash payments, fire the cash-drawer-open audit event so the
      // hardware kick-out hook (or just the audit log) records it.
      if (paymentMethod === "cash") {
        try {
          const dr = await axios.post(`${API}/cash-drawer/open`, {
            reason: "cash payment",
            order_id: order.id,
            amount: adjustedSubtotal + tax + tipAmount,
          });
          await runBrowserHandoffs(dr.data?.browser_handoff_results);
        } catch { /* non-fatal — payment succeeded */ }
      }

      // Burn redeemed points if any.
      if (loyaltyMember && redeemPoints > 0) {
        try {
          await axios.post(`${API}/loyalty/redeem`, {
            customer_id: loyaltyMember.id, points: redeemPoints,
          });
        } catch { /* non-fatal */ }
      }
      // Log perk redemption for audit / cooldown reporting. If the
      // server rejects with 400 (perk on cooldown), surface it as an
      // info toast — the payment already succeeded, we just skip the
      // audit log rather than roll back the whole transaction.
      if (loyaltyMember && selectedPerk && perkDiscount > 0) {
        try {
          await axios.post(`${API}/loyalty/redeem-perk`, {
            customer_id: loyaltyMember.id,
            tier_name: loyaltyMember.tier || "bronze",
            perk_id: selectedPerk.id,
            perk_label: selectedPerk.label,
            order_id: order.id,
            discount_amount: perkDiscount,
          });
        } catch (perkErr) {
          const status = perkErr?.response?.status;
          const detail = perkErr?.response?.data?.detail;
          if (status === 400 && typeof detail === "string") {
            toast.warning(detail);
          }
          /* otherwise non-fatal — audit log gap */
        }
      }
      // Award points on the post-discount amount.
      if (loyaltyMember) {
        try {
          await axios.post(`${API}/loyalty/earn`, {
            customer_id: loyaltyMember.id, order_total: adjustedSubtotal + tax,
          });
        } catch { /* non-fatal */ }
      }

      setStep(STEPS.SUCCESS);
      toast.success("Payment processed successfully!");
      setTimeout(() => { onComplete(response.data); reset(); }, 1600);
    } catch (e) {
      // Server-side payment failure. Also persist so the operator can read it
      // — the previous behaviour toasted for 4s which is way too fast for a
      // decline or a Cloudflare-blocked API round-trip.
      const detail = e.response?.data?.detail || e.message || "Payment failed";
      const isNetwork = !e.response;
      const title = isNetwork ? "Payment server unreachable" : "Payment declined";
      setPaymentError({ title, detail, code: e.response?.status });
      toast.error(title, {
        description: detail + (e.response?.status ? ` (HTTP ${e.response.status})` : ""),
        duration: Infinity,
        id: "card-error-toast",
        closeButton: true,
      });
      setIsProcessing(false);
    }
  };

  // -------- Headline + summary chip --------
  const titles = {
    [STEPS.TIP]: "Add a Tip",
    [STEPS.LOYALTY]: "Loyalty",
    [STEPS.PAY]: "Process Payment",
    [STEPS.WAIT]: "On the Customer Screen",
    [STEPS.SUCCESS]: "Payment Successful",
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-[520px]" data-testid="payment-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{titles[step]}</DialogTitle>
        </DialogHeader>

        {/* Persistent summary chip */}
        {step !== STEPS.SUCCESS && (
          <SummaryChip
            subtotal={subtotal}
            tax={tax}
            tip={tipAmount}
            discount={discountFromPoints}
            perkDiscount={perkDiscount}
            perkLabel={selectedPerk?.label}
            surcharge={cashDiscountSurcharge}
            surchargeLabel={`${cashDiscountLabel} (${cashDiscountPct}%)`}
            total={total}
          />
        )}

        {/* ---- STEP: TIP ---- */}
        {step === STEPS.TIP && (
          <TipStep
            subtotal={subtotal}
            tipPresets={tipPresets}
            tipAmount={tipAmount} setTipAmount={setTipAmount}
            customTipOpen={customTipOpen} setCustomTipOpen={setCustomTipOpen}
            customTipInput={customTipInput} setCustomTipInput={setCustomTipInput}
            commitCustomTip={commitCustomTip}
            applyTipPct={applyTipPct}
            onCancel={close}
            onNext={async () => {
              // If a customer display is configured, push the wizard to that screen now.
              const opened = await openCustomerDisplayPopup();
              if (!opened) setStep(STEPS.LOYALTY);
            }}
          />
        )}

        {/* ---- STEP: LOYALTY ---- */}
        {step === STEPS.LOYALTY && (
          <LoyaltyStep
            loyaltyMember={loyaltyMember} setLoyaltyMember={setLoyaltyMember}
            loyaltyPhone={loyaltyPhone} setLoyaltyPhone={setLoyaltyPhone}
            loyaltyLookupLoading={loyaltyLookupLoading}
            handleLookupPhone={handleLookupPhone}
            showEnroll={showEnroll} setShowEnroll={setShowEnroll}
            enrollName={enrollName} setEnrollName={setEnrollName}
            enrollEmail={enrollEmail} setEnrollEmail={setEnrollEmail}
            handleEnroll={handleEnroll}
            subtotal={subtotal}
            redeemPoints={redeemPoints} setRedeemPoints={setRedeemPoints}
            memberPerks={memberPerks}
            selectedPerk={selectedPerk} setSelectedPerk={setSelectedPerk}
            handleDeclineLoyalty={handleDeclineLoyalty}
            onBack={() => setStep(STEPS.TIP)}
            onContinue={() => setStep(STEPS.PAY)}
          />
        )}

        {/* ---- STEP: WAIT (customer screen) ---- */}
        {step === STEPS.WAIT && (
          <div className="py-10 text-center space-y-4" data-testid="wizard-wait-step">
            <Spinner size={48} className="animate-spin mx-auto text-accent" />
            <div>
              <p className="font-medium">Customer is selecting tip &amp; loyalty</p>
              <p className="text-sm text-muted-foreground">on the secondary screen.</p>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                // Fallback path when the CFD is broken/unresponsive: the
                // operator takes over on the POS and runs the tip →
                // loyalty → pay flow themselves. Latch the "declined"
                // flag so Next: Loyalty (and the auto-open on modal
                // mount) never bounces us back to the CFD/WAIT screen.
                cfdDeclinedRef.current = true;
                try {
                  const ch = channelRef.current || new BroadcastChannel(CHANNEL_NAME);
                  ch.postMessage({ type: "end_order" });
                } catch { /* noop */ }
                setStep(STEPS.TIP);
              }}
              data-testid="wizard-wait-cancel"
            >
              Cancel &amp; do it here instead
            </Button>
          </div>
        )}

        {/* ---- STEP: PAY ---- */}
        {step === STEPS.PAY && (
          <div className="space-y-4 mt-2" data-testid="wizard-pay-step">
            {gatewayStatus?.sandbox && (
              <div
                className="rounded-md border-2 border-red-500 bg-red-50 dark:bg-red-950/40 dark:border-red-600 px-4 py-3 flex items-start gap-3"
                data-testid="sandbox-warning-banner"
                role="alert"
              >
                <div className="mt-0.5 shrink-0 h-6 w-6 rounded-full bg-red-500 text-white flex items-center justify-center font-bold text-sm">!</div>
                <div className="min-w-0 text-sm leading-snug">
                  <div className="font-bold text-red-900 dark:text-red-200 uppercase tracking-wide text-xs">
                    Sandbox Mode — No Real Charges
                  </div>
                  <div className="text-red-900/90 dark:text-red-200/90 mt-1">
                    Card transactions submitted here are recorded locally as &quot;approved&quot; but <strong>never reach Authorize.net</strong>. Customers are not charged.
                    <br />
                    Fix: Settings → Company → <strong>Payment Gateway</strong> → enter your live Authorize.net credentials.
                  </div>
                </div>
              </div>
            )}
            <Tabs value={paymentMethod} onValueChange={setPaymentMethod}>
              <TabsList className={`grid mb-2 ${posSettings.external_terminal_type ? "grid-cols-4" : "grid-cols-3"}`}>
                <TabsTrigger value="card" className="gap-2" data-testid="card-tab">
                  <CreditCard size={18} /> Card
                </TabsTrigger>
                {posSettings.external_terminal_type && (
                  <TabsTrigger value="pinpad" className="gap-2" data-testid="pinpad-tab">
                    <CreditCard size={18} weight="duotone" /> Pinpad
                  </TabsTrigger>
                )}
                <TabsTrigger value="cash" className="gap-2" data-testid="cash-tab">
                  <Money size={18} /> Cash
                </TabsTrigger>
                <TabsTrigger value="gift_card" className="gap-2" data-testid="gift-card-tab">
                  <CreditCard size={18} weight="fill" /> Gift Card
                </TabsTrigger>
              </TabsList>

              <TabsContent value="card" className="space-y-3">
                <CardPanel
                  swipeTracks={swipeTracks}
                  clearCardFields={clearCardFields}
                  firstName={firstName} setFirstName={setFirstName}
                  lastName={lastName} setLastName={setLastName}
                  cardNumber={cardNumber} setCardNumber={setCardNumber}
                  expDate={expDate} setExpDate={setExpDate}
                  cvv={cvv} setCvv={setCvv}
                  formatCardNumber={formatCardNumber}
                  formatExpDate={formatExpDate}
                />
              </TabsContent>

              <TabsContent value="pinpad">
                <PinpadPanel total={total} terminalType={posSettings.external_terminal_type} />
              </TabsContent>

              <TabsContent value="cash">
                <CashPanel total={total} orderId={order.id} />
              </TabsContent>

              <TabsContent value="gift_card" className="space-y-3">
                <GiftCardPanel
                  total={total}
                  gcCode={gcCode} setGcCode={setGcCode}
                  gcLookup={gcLookup} setGcLookup={setGcLookup}
                  gcLookupErr={gcLookupErr} setGcLookupErr={setGcLookupErr}
                  gcLookingUp={gcLookingUp} setGcLookingUp={setGcLookingUp}
                />
              </TabsContent>
            </Tabs>

            {paymentError && (
              <div
                className="rounded-lg border-2 border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-900 p-3 text-sm"
                data-testid="payment-error-banner"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-red-800 dark:text-red-200">
                      {paymentError.title}
                      {paymentError.code ? (
                        <span className="ml-2 text-xs font-mono opacity-70">
                          [code {paymentError.code}]
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-1 text-red-900/90 dark:text-red-200/90 whitespace-pre-wrap">
                      {paymentError.detail}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      // Also close the sonner toast — otherwise the operator
                      // is left with the same red popup floating over the POS
                      // and would have to click its ✕ (which visually reads
                      // like "cancel this whole payment"). Two dismisses, one
                      // click.
                      setPaymentError(null);
                      toast.dismiss("card-error-toast");
                    }}
                    className="text-xs text-red-700 dark:text-red-300 hover:underline shrink-0"
                    data-testid="payment-error-dismiss"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}

            <DialogFooter className="flex !justify-between gap-2 sm:!justify-between">
              <Button
                variant="outline"
                onClick={() => {
                  // SECURITY: never carry card data across step transitions.
                  clearCardFields();
                  // If CFD is in play, send the customer back into the
                  // checkout flow so they can retry their tip/loyalty; else
                  // step back to the local LOYALTY picker.
                  if (posSettings.customer_display_enabled) {
                    try {
                      const ch = channelRef.current || new BroadcastChannel(CHANNEL_NAME);
                      ch.postMessage({ type: "start_order", orderId: order.id });
                    } catch { /* noop */ }
                    setStep(STEPS.WAIT);
                  } else {
                    setStep(STEPS.LOYALTY);
                  }
                }}
                disabled={isProcessing}
                data-testid="pay-back-btn"
              >
                Back
              </Button>
              <Button
                onClick={handleProcessPayment}
                disabled={isProcessing || (paymentMethod === "card" && !isCardValid) || (paymentMethod === "gift_card" && (!gcLookup || gcLookup.status === "voided" || (gcLookup.current_balance ?? 0) < total))}
                className="gap-2 min-w-[160px]"
                data-testid="confirm-payment-btn"
              >
                {isProcessing ? (
                  <><Spinner size={18} className="animate-spin" /> {paymentMethod === "pinpad" ? "Waiting on pinpad…" : "Processing…"}</>
                ) : (
                  <><CheckCircle size={18} /> {paymentMethod === "pinpad" ? `Send to Pinpad ($${total.toFixed(2)})` : `Pay $${total.toFixed(2)}`}</>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* ---- STEP: SUCCESS ---- */}
        {step === STEPS.SUCCESS && (
          <SuccessStep
            total={total}
            loyaltyMember={loyaltyMember}
            pointsAwarded={Math.floor(adjustedSubtotal + tax)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

const SummaryChip = ({
  subtotal,
  tax,
  tip,
  discount,
  total,
  perkDiscount = 0,
  perkLabel,
  surcharge = 0,
  surchargeLabel,
}) => (
  <div className="bg-secondary/40 rounded-lg p-3 text-sm space-y-1 font-mono" data-testid="wizard-summary">
    <div className="flex justify-between"><span className="font-sans">Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
    {discount > 0 && (
      <div className="flex justify-between text-accent"><span className="font-sans">Loyalty Discount</span><span>-${discount.toFixed(2)}</span></div>
    )}
    {perkDiscount > 0 && (
      <div className="flex justify-between text-accent" data-testid="wizard-summary-perk">
        <span className="font-sans">{perkLabel || "Perk"}</span><span>-${perkDiscount.toFixed(2)}</span>
      </div>
    )}
    <div className="flex justify-between"><span className="font-sans">Tax</span><span>${tax.toFixed(2)}</span></div>
    {tip > 0 && <div className="flex justify-between text-accent"><span className="font-sans">Tip</span><span>${tip.toFixed(2)}</span></div>}
    {surcharge > 0 && (
      <div className="flex justify-between text-amber-600" data-testid="wizard-summary-surcharge">
        <span className="font-sans">{surchargeLabel || "Non-Cash Adjustment"}</span>
        <span>+${surcharge.toFixed(2)}</span>
      </div>
    )}
    <Separator className="my-1" />
    <div className="flex justify-between font-semibold text-base"><span className="font-sans">Total</span><span data-testid="wizard-summary-total">${total.toFixed(2)}</span></div>
  </div>
);


export default PaymentModal;
